import React, { useEffect, useState, useContext, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { HiPrinter, HiDocumentDownload, HiArrowLeft } from "react-icons/hi";

import { voucherService } from "../../services/voucherService";
import GeneralModal from "../../helpers/GeneralModal";
import { createModal } from "react-modal-promise";
import LogicContext from "../../Context/LogicContext";

import { SetVoucherListAtom, GetVoucherListAtom } from "../../store/voucherAtom";

import VoucherDetailContent from "./VoucherDetailsContent";
import VoucherScroll from "./VoucherScroller";

export default function VoucherDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const logicContext = useContext(LogicContext);

  const setVoucherAtomData = SetVoucherListAtom();
  const getVoucherAtomData = GetVoucherListAtom();

  const [voucherDetails, setVoucherDetails] = useState({});
  const [loading, setLoading] = useState(true);

  const myPromiseModal = createModal(GeneralModal);
  const componentRef = useRef();

  const [currModuleId, currModuleName, currUserMenu] = pathname.split("/").slice(1, 4);

  useEffect(() => {
    document.title = "Voucher Details";
    loadVoucherDetails(id);
  }, [id]);

  const loadVoucherDetails = async (voucherId) => {
    setLoading(true);
    try {
      const res = await voucherService.voucherDetails(voucherId);
      if (res?.data?.length) {
        setVoucherDetails(res.data[0]);
      }
    } catch (err) {
      logicContext.showToast("Failed to load voucher details", "error");
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => navigate(-1);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `Voucher_${voucherDetails?.Voucher_Number || "Details"}`,
    pageStyle: `
      @page { size: A4; margin: 20mm; }
      @media print {
        .no-print { display: none !important; }
        body { font-family: 'Inter', Arial, sans-serif; font-size: 11pt; }
        .print-content { width: 100%; max-width: 210mm; margin: 0 auto; }
        .print-content section { box-shadow: none; border: 1px solid #e5e7eb; margin-bottom: 20px; padding: 16px; }
        h2 { font-size: 16pt; margin-bottom: 12px; }
        .text-sm { font-size: 10pt; }
      }
    `,
  });

  const viewCustomer = (customerId) => {
    const url = `/${currModuleId}/${currModuleName}/customers/customer-details/${customerId}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };


  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={goBack}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <HiArrowLeft size={20} />
              </button>
              <h1 className="text-xl font-semibold text-gray-900">
                Voucher Details
              </h1>
              {voucherDetails?.Voucher_Number && (
                <span className="text-sm text-gray-500 font-mono">
                  #{id}
                </span>
              )}
            </div>

            <div className="flex items-center gap-3 no-print">
              <button
                onClick={handlePrint}
                title="Print Voucher"
                className="p-2.5 rounded-lg hover:bg-gray-100 text-gray-700 transition-colors"
              >
                <HiPrinter size={22} />
              </button>

              <button
                onClick={handlePrint} // can be changed to real PDF export later
                title="Export as PDF"
                className="p-2.5 rounded-lg hover:bg-gray-100 text-gray-700 transition-colors"
              >
                <HiDocumentDownload size={22} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - Voucher List */}
        <aside className="w-80 xl:w-96 flex-shrink-0 border-r bg-white overflow-hidden no-print">
          <VoucherScroll  />
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
            <div ref={componentRef} className="print-content">
              <VoucherDetailContent
                voucherDetails={voucherDetails}
                viewCustomer={viewCustomer}
              />
            </div>
          )}
        </main>
      </div>
    </div>
  );
}